<script lang="ts" module>
	const ages = [
		'15-19',
		'20-24',
		'25-29',
		'30-34',
		'35-39',
		'40-44',
		'45-49',
		'50-54',
		'55-59',
		'60-64',
		'65-69',
		'70-74',
		'75-79',
		'80+'
	];

	const premature = (age: AlcoholAgeGroup) =>
		ages.indexOf(age) <= 10 ? 'premature' : 'not_premature';
</script>

<script lang="ts">
	import data, {
		type AlcoholScenario,
		type AlcoholDisease,
		type AlcoholAgeGroup
	} from '$lib/data/alcohol.csv';
	import { getCopy } from '$lib/lang/copy.svelte';
	import { formatNumber } from '$lib/utils/format';
	import { flip } from 'svelte/animate';
	import { rollup, sum, rollups } from 'd3';
	import md from '$lib/utils/md';

	import { RadioGroup } from 'bits-ui';
	import Counter from '$lib/components/fragments/Counter.svelte';
	import Tooltip from '$lib/components/fragments/Tooltip.svelte';

	const copy = $derived(getCopy().alcohol.chart);
	const scenarios = $derived<{ label: string; inline: string; value: AlcoholScenario }[]>([
		{ ...copy?.scenarios[0], value: 'One less drink' },
		{ ...copy?.scenarios[1], value: 'Two less drinks' },
		{ ...copy?.scenarios[2], value: 'Three less drinks' }
	]);
	const toplines = $derived(
		rollup(
			data,
			(v) => sum(v, (d) => d.count),
			(d) => d.scenario
		)
	);

	// Selected scenario bound to radio input
	let selectedScenario = $state<AlcoholScenario>('One less drink');

	// Filtered data for selected scenario
	let scenarioData = $derived(data.filter((d) => d.scenario === selectedScenario));

	// Rolled data for bars and disease nodes
	let groupedData = $derived(
		rollups(
			scenarioData,
			(v) => sum(v, (d) => d.count),
			(d) => d.gender,
			(d) => d.disease,
			(d) => premature(d.age)
		)
	);

	let genderData = $derived(
		rollup(
			scenarioData,
			(v) => sum(v, (d) => d.count),
			(d) => d.gender
		)
	);

	let diseaseData = $derived(
		rollups(
			scenarioData,
			(v) => sum(v, (d) => d.count),
			(d) => d.disease
		)
	);

	let hovered = $state<AlcoholDisease>();

	let hoverData = $derived(
		rollups(
			scenarioData.filter((d) => d.disease === hovered),
			(v) => sum(v, (d) => d.count),
			(d) => premature(d.age),
			(d) => d.gender
		)
	);
</script>

<div class="content-well-lg flex flex-col items-center gap-8">
	<div class="border-gray-dark flex w-full flex-col items-center gap-8 border-t pt-8">
		<div class="flex flex-col items-center gap-2 max-md:text-center">
			<p class="heading-4 text-blue-light">
				<Counter value={toplines.get(selectedScenario) || 0}>
					{#snippet children(value)}
						<span class="text-blue-lighter">{formatNumber(value)}</span>
					{/snippet}
				</Counter>
				{copy.topline}
			</p>
			<p class="subtitle-1">
				{copy.description}
				<span class="text-blue-lighter"
					>{scenarios.find((s) => s.value === selectedScenario)?.inline}</span
				>
			</p>
		</div>
		<div class="flex flex-col items-center gap-3">
			<RadioGroup.Root class="flex gap-2 md:gap-4" bind:value={selectedScenario}>
				{#each scenarios as { label, value } (value)}
					<RadioGroup.Item
						class="subtitle-1 data-[state=checked]:bg-blue border-gray-dark data-[state=checked]:border-blue text-blue-light cursor-pointer rounded-lg border p-2 leading-none transition-all hover:scale-102 data-[state=checked]:text-white max-md:text-sm md:p-4"
						{value}
					>
						{label}
					</RadioGroup.Item>
				{/each}
			</RadioGroup.Root>
			<p class="microcopy-1 text-gray-darker flex items-center gap-2 max-md:text-xs">
				<iconify-icon icon="ic:baseline-ads-click" class="text-lg"></iconify-icon>
				{copy.prompt}
			</p>
		</div>
	</div>

	<figure class="w-full">
		<div class="border-gray-dark flex flex-col gap-6 border-y py-6 md:gap-10 md:py-10">
			<div class="border-gray-dark relative mb-22 border-r pt-16">
				<div class="absolute top-0 right-1 max-w-[250px] text-right">
					<p class="heading-5 mb-1 text-black">{formatNumber(toplines.get('No drinks') ?? 0)}</p>
					<p class="over-line-3 text-gray-darker text-xs">{copy.label}</p>
				</div>

				<div class="bg-gray/50 border-gray-dark flex h-50 w-full">
					{#each groupedData as [gender, diseaseGroups] (gender)}
						{#each diseaseGroups as [disease, ageGroups], i (disease)}
							{@const genderValue = genderData.get(gender)}
							{@const diseaseValue = ageGroups.reduce((sum, d) => sum + d[1], 0)}
							{@const width = (100 * diseaseValue) / (toplines.get('No drinks') ?? 1)}
							{@const active = hovered === disease || !hovered}
							<div
								class={['relative flex cursor-crosshair flex-col transition-all']}
								animate:flip={{ duration: 200 }}
								role="presentation"
								onmouseover={() => (hovered = disease)}
								onmouseout={() => (hovered = undefined)}
								onfocus={() => (hovered = disease)}
								onblur={() => (hovered = undefined)}
								style:width="{width}%"
							>
								{#each ageGroups as [age, ageValue] (age)}
									<div
										class={[
											'border-cream w-full border transition-all',
											gender === 'Men'
												? age === 'premature'
													? 'bg-blue-light'
													: 'bg-blue-light/50'
												: age === 'premature'
													? 'bg-yellow'
													: 'bg-yellow/50',
											age === 'premature' ? 'order-1' : 'order-2',
											hovered && !active && 'opacity-30'
										]}
										style:height="{(100 * ageValue) / diseaseValue}%"
									></div>
								{/each}

								{#if i === 0}
									<div
										class={[
											'heading-4 pointer-events-none absolute top-full left-0 mt-3 flex flex-col gap-2',
											gender === 'Women' && selectedScenario === 'No drinks'
												? 'max-sm:-translate-x-1/3 max-sm:items-end'
												: ''
										]}
									>
										<Counter value={genderValue || 0}>
											{#snippet children(value)}
												{formatNumber(value)}
											{/snippet}
										</Counter>
										<span
											class={[
												'over-line-3 w-fit p-2 whitespace-nowrap',
												gender === 'Men' ? 'bg-blue-light text-white' : 'text-blue bg-yellow'
											]}>{copy.total} {copy.genders[gender]}</span
										>
									</div>
								{/if}
							</div>
						{/each}
					{/each}
				</div>
			</div>

			<div class="border-gray-dark w-full border-t pt-8">
				<p class="over-line-1">{copy.subtitile}</p>
				<div class="mt-6 grid grid-cols-2 flex-wrap gap-4 sm:flex">
					{#each diseaseData.sort((a, b) => b[1] - a[1]) as [disease, value] (disease)}
						{@const active = hovered === disease}
						<div
							animate:flip={{ duration: 200 }}
							role="presentation"
							onmouseover={() => (hovered = disease)}
							onmouseout={() => (hovered = undefined)}
							onfocus={() => (hovered = disease)}
							onblur={() => (hovered = undefined)}
							class={[
								'bg-gray-light flex w-full cursor-crosshair flex-col items-center justify-center gap-2 rounded p-4 transition-all sm:w-[189px]',
								active && '!bg-blue text-white'
							]}
						>
							<p class="subtitle-2 text-center">{copy.cancer_types[disease]}</p>
							<Counter {value}>
								{#snippet children(value)}
									<span class="heading-4">{formatNumber(value)}</span>
								{/snippet}
							</Counter>
						</div>
					{/each}
				</div>
			</div>
		</div>
	</figure>

	<div class="content-well-sm source text-gray-darker">
		{@html md(copy.methodology)}
	</div>
</div>

{#if hovered}
	<Tooltip maxWidth={300} class="w-full">
		<div class="flex flex-col gap-3">
			<div>
				<p class="subtitle-2 w-full font-bold">{copy.cancer_types[hovered]}</p>
				<p class="microcopy-2 text-gray-darker border-b border-black pt-1 pb-2">
					{formatNumber(diseaseData.find(([d]) => d === hovered)?.[1] || 0)}
					{copy.topline}
				</p>
			</div>
			<table class="w-full">
				<thead>
					<tr>
						<th></th>
						{#each hoverData[0][1] as [gender] (gender)}
							<th>{copy.genders[gender]}</th>
						{/each}
					</tr>
				</thead>
				<tbody>
					{#each hoverData as [age, genders] (age)}
						<tr>
							<th>{copy.ages[age]}</th>
							{#each genders as [gender, value] (gender)}
								<td
									class={[
										'p-1 text-center',
										gender === 'Men'
											? age === 'premature'
												? 'bg-blue-light text-cream'
												: 'bg-blue-light/50 text-cream'
											: age === 'premature'
												? 'bg-yellow text-blue'
												: 'bg-yellow/50 text-blue'
									]}>{formatNumber(value)}</td
								>
							{/each}
						</tr>
					{/each}
					<tr>
						<th>{copy.total}</th>
						{#each hoverData[0][1] as [gender] (gender)}
							{@const genderTotal = hoverData.reduce(
								(sum, [, genders]) => sum + (genders.find(([g]) => g === gender)?.[1] || 0),
								0
							)}
							<td class="p-1 text-center">{formatNumber(genderTotal)}</td>
						{/each}
					</tr>
				</tbody>
			</table>
		</div>
	</Tooltip>
{/if}

<style lang="postcss">
	@reference '$lib/styles/app.css';

	th {
		@apply microcopy-2 text-gray-darker text-left font-normal;
	}
	tbody th,
	td {
		@apply border-gray-dark border-y;
	}
</style>
